import classNames from 'classnames';
import React, { memo, useState } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import Icon from './Icon';
import { useSelector } from 'react-redux';
import { AppState } from '@/store';
import ErrorBoundary from '@components/tailwind/ErrorBoundary';
import { useVariation } from '@hooks/useVariation';
import ProductAvailabilityVariant from './ProductAvailabilityVariant';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const ProductAvailability = ({
  stock,
  quantityPerOrderLimit = null,
  processingDays = 0,
  className = '',
}) => {
  const isEnhancedPDPVariant = useVariation('Enhanced PDP');

  if (isEnhancedPDPVariant)
    return (
      <ProductAvailabilityVariant
        stock={stock}
        quantityPerOrderLimit={quantityPerOrderLimit}
        processingDays={processingDays}
        className={className}
      />
    );

  if (!['inStock', 'lowStock', 'soldOut'].includes(stock)) return null;

  return (
    <div className={classNames('tw:flex tw:gap-2', { 'tw:items-center': stock === 'soldOut' }, className)}>
      <div className='tw:w-[24px] tw:text-center'>
        {(stock === 'inStock' || stock === 'soldOut') && (
          <Icon url='/assets/images/rtic/nav/calendar-icon.svg' size={24} color='var(--brand-black)' />
        )}
        {stock === 'lowStock' && <FaExclamationTriangle size={20} className='tw:text-brand-black' />}
      </div>
      <div>
        <>
          <p
            className={classNames('tw:mb-0 tw:text-sm tw:font-bold tw:leading-4', {
              'tw:text-brand-black': stock === 'inStock',
              'tw:text-warning': stock === 'lowStock',
              'tw:text-red-700': stock === 'soldOut',
            })}>
            {stock === 'inStock' && 'In Stock'}
            {stock === 'lowStock' && 'Low Stock'}
            {stock === 'soldOut' && 'Sold Out'}
          </p>
        </>
        {stock !== 'soldOut' && (
          <div className='tw:mb-0 tw:text-sm tw:font-normal tw:text-gray-dark'>
            {(processingDays ?? 0) > 0 ? (
              <>
                Please Allow up to {processingDays} {processingDays === 1 ? 'Day' : 'Days'} to Process your
                Order
              </>
            ) : (
              <ErrorBoundary message='Ready to Ship' messageOnly>
                <CutoffLabel />
              </ErrorBoundary>
            )}

            {quantityPerOrderLimit && (
              <span className='tw:block'>Limit {quantityPerOrderLimit} per order</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CutoffLabelTest = () => {
  const [_day, setDay] = useState(0);
  const [_hour, setHour] = useState(0);
  const { cutoffTimestamp, cutoffSeconds } = useSelector((state: AppState) => state.site.shipping);
  const cutoff = dayjs(cutoffTimestamp);

  let now = dayjs();

  const parse = (value) => {
    const parsed = parseInt(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const hour = parse(_hour);
  const day = parse(_day);

  if (hour) now = now.add(hour, 'hour');
  if (day) now = now.add(day, 'day');

  const set = (setter) => (e) => {
    setter(e.target.value);
  };

  return (
    <div>
      <CutoffLabel now={now} />
      <hr />
      cutoff (local):
      <br />
      {cutoff.format('MM/DD/YYYY, h:mm:ss A')}
      <br />
      "now":
      <br />
      {now.format('MM/DD/YYYY, h:mm:ss A')}
      <br />
      delta{' '}
      <input
        type='text'
        onChange={set(setDay)}
        value={_day}
        style={{ width: '2em', textAlign: 'center' }}
        onFocus={(e) => e.target.select()}
      />
      d
      <input
        type='text'
        onChange={set(setHour)}
        value={_hour}
        style={{ width: '2em', textAlign: 'center' }}
        onFocus={(e) => e.target.select()}
      />
      h
    </div>
  );
};

// Using dayjs for date/time handling

const days = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const CutoffLabel = ({ now = dayjs() }) => {
  const { cutoffTimestamp } = useSelector((state: AppState) => state.site.shipping);
  const flags = useSelector((state: AppState) => state.site.flags);

  if (!cutoffTimestamp || !flags?.readyToShip) return <>Ready to Ship</>;

  const cutoff = dayjs(cutoffTimestamp);
  const isBeforeCutoff = cutoff.isAfter(now);
  const isCutoffSameDay = cutoff.isSame(now, 'day');

  const day = now.day();
  const hours = cutoff.format('h');
  const minutes = cutoff.minute();
  const seconds = cutoff.second();
  const ampm = cutoff.format('A').toLowerCase();

  // Cutoff label without timezone
  const cutoffLabel =
    (seconds > 0 ? [hours, minutes, seconds] : minutes > 0 ? [hours, minutes] : [hours]).join(':') + ampm;

  const includeSaturday = flags.readyToShip?.[0]?.includeSaturday || false;
  const includeSunday = flags.readyToShip?.[0]?.includeSunday || false;
  const businessDays = [1, 2, 3, 4, 5];
  if (includeSaturday) businessDays.push(6);
  if (includeSunday) businessDays.unshift(0);
  const isBusinessDay = businessDays.includes(day);
  const nextDay = businessDays.find((d) => d > day) || businessDays[0];
  const nextLabel = day + 1 === nextDay || (day === 6 && nextDay === 0) ? 'Tomorrow' : days[nextDay];

  return (
    <div>
      {isBusinessDay && isCutoffSameDay && isBeforeCutoff ? (
        <>
          Order by {cutoffLabel}
          <br />
          Ships Today!
        </>
      ) : (
        <>Ships {nextLabel}</>
      )}
    </div>
  );
};

export default memo(ProductAvailability);
